/**
 * VendorLedgerService
 * Service for managing vendor financial ledgers
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for vendor ledger CRUD operations with vendor-specific queries
 */
export class VendorLedgerService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorLedgerService.DEFAULT_SERVICE_PATH,
            basePath: VendorLedgerService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get all ledger records for a specific vendor
     * @param vendorId - The vendor ID
     * @returns Array of vendor ledgers
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all ledger records for a specific vendor with full response
     * @param vendorId - The vendor ID
     * @returns ResponseData with array of vendor ledgers
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
    /**
     * Get a ledger statement for a vendor within a date range
     * @param vendorId - The vendor ID
     * @param from - Start date string
     * @param to - End date string
     * @returns Vendor statement with entries and summary totals
     */
    async getStatement(vendorId, from, to) {
        const response = await this.getStatementRaw(vendorId, from, to);
        if (!response.data) {
            throw new Error(`Failed to retrieve statement for vendor: vendorId=${vendorId}`);
        }
        return response.data;
    }
    /**
     * Get a ledger statement for a vendor within a date range with full response
     * @param vendorId - The vendor ID
     * @param from - Start date string
     * @param to - End date string
     * @returns ResponseData with vendor statement
     */
    getStatementRaw(vendorId, from, to) {
        const url = this.buildUrl(`vendor/${vendorId}/statement?from=${from}&to=${to}`);
        return this.http.get(url);
    }
    /**
     * Credit a vendor's ledger
     * @param vendorId - The vendor ID
     * @param data - Credit data
     * @returns The created ledger entry
     */
    async creditVendor(vendorId, data) {
        const response = await this.creditVendorRaw(vendorId, data);
        if (!response.data) {
            throw new Error(`Failed to credit vendor ledger: vendorId=${vendorId}`);
        }
        return response.data;
    }
    /**
     * Credit a vendor's ledger with full response
     * @param vendorId - The vendor ID
     * @param data - Credit data
     * @returns ResponseData with the created ledger entry
     */
    creditVendorRaw(vendorId, data) {
        const url = this.buildUrl(`vendor/${vendorId}/credit`);
        return this.http.post(url, data);
    }
    // ==================== MODEL VIEW QUERIES ====================
    /**
     * Get all vendor ledgers as model views
     * @returns Array of vendor ledger views
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor ledgers as model views with full response
     * @returns ResponseData with array of vendor ledger views
     */
    getViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get vendor ledger model view by ID
     * @param id - Ledger ID
     * @returns The vendor ledger view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`VendorLedger view not found: id=${id}`);
        }
        return response.data;
    }
    /**
     * Get vendor ledger model view by ID with full response
     * @param id - Ledger ID
     * @returns ResponseData with vendor ledger view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`${id}/view`);
        return this.http.get(url);
    }
    /**
     * Get all ledger model views for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor ledger views
     */
    async getViewsByVendor(vendorId) {
        const response = await this.getViewsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all ledger model views for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor ledger views
     */
    getViewsByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}/views`);
        return this.http.get(url);
    }
}
VendorLedgerService.DEFAULT_SERVICE_PATH = 'vendors';
VendorLedgerService.BASE_PATH = 'api/vendor-ledgers';
//# sourceMappingURL=VendorLedgerService.js.map